import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * AI Invoice Service - Handles AI summary generation for all invoice types
 *
 * This service provides a unified interface for generating AI-powered invoice summaries
 * across different invoice types: Customer, Recurring, Supplier, and Scheduled invoices.
 *
 * All invoice types use the same AI model (gpt-4o-mini) and settings for consistency.
 */
@Injectable({
  providedIn: 'root'
})
export class AIInvoiceService {

  // Mirror quote service: use environment.url so app interceptors/auth apply
  private readonly apiUrl = `${environment.url}/ai/invoices/summary`;

  constructor(private http: HttpClient) { }

  /**
   * Generate AI summary for any invoice type
   *
   * @param invoiceData - The invoice data to generate summary from
   * @param invoiceType - Type of invoice (customer_invoices, recurring_invoices, supplier_invoices, scheduled_invoices)
   * @param promptTemplate - Optional custom prompt template from admin prompt system
   * @returns Observable with AI-generated summary
   */
  generateInvoiceSummary(invoiceData: any, invoiceType: 'customer_invoices' | 'recurring_invoices' | 'supplier_invoices' | 'scheduled_invoices', promptTemplate?: string): Observable<any> {
    // Create request parameters following system pattern
    let body = new URLSearchParams();

    // Wrap data in 'data' field as per system convention
    const requestData = {
      invoice_data: invoiceData,
      invoice_type: invoiceType,
      model: 'gpt-4o-mini',
      temperature: 0.7,
      module_type: 'invoices'
    };

    // Add prompt template if provided (connects to admin prompt system)
    if (promptTemplate) {
      requestData['prompt_template'] = promptTemplate;
    }

    // Append data as JSON string (following system pattern)
    body.append('data', JSON.stringify(requestData));

    // Removed verbose console logging of invoice data to prevent leaking sensitive details

    // Send as URLSearchParams (following system auth pattern)
    return this.http.post(this.apiUrl, body.toString());
  }

  /**
   * Generate AI summary for Customer Invoice
   *
   * @param customerInvoiceData - Customer invoice data
   * @param promptTemplate - Optional custom prompt template from admin prompt system
   * @returns Observable with AI-generated summary
   */
  generateCustomerInvoiceSummary(customerInvoiceData: any, promptTemplate?: string): Observable<any> {
    return this.generateInvoiceSummary(customerInvoiceData, 'customer_invoices', promptTemplate);
  }

  /**
   * Generate AI summary for Recurring Customer Invoice
   *
   * @param recurringInvoiceData - Recurring invoice data
   * @param promptTemplate - Optional custom prompt template from admin prompt system
   * @returns Observable with AI-generated summary
   */
  generateRecurringInvoiceSummary(recurringInvoiceData: any, promptTemplate?: string): Observable<any> {
    return this.generateInvoiceSummary(recurringInvoiceData, 'recurring_invoices', promptTemplate);
  }

  /**
   * Generate AI summary for Supplier Invoice
   *
   * @param supplierInvoiceData - Supplier invoice data
   * @param promptTemplate - Optional custom prompt template from admin prompt system
   * @returns Observable with AI-generated summary
   */
  generateSupplierInvoiceSummary(supplierInvoiceData: any, promptTemplate?: string): Observable<any> {
    return this.generateInvoiceSummary(supplierInvoiceData, 'supplier_invoices', promptTemplate);
  }

  /**
   * Generate AI summary for Scheduled Invoice
   *
   * @param scheduledInvoiceData - Scheduled invoice data
   * @param promptTemplate - Optional custom prompt template from admin prompt system
   * @returns Observable with AI-generated summary
   */
  generateScheduledInvoiceSummary(scheduledInvoiceData: any, promptTemplate?: string): Observable<any> {
    return this.generateInvoiceSummary(scheduledInvoiceData, 'scheduled_invoices', promptTemplate);
  }

  /**
   * Refine a single time entry description using AI to make it more professional
   *
   * @param description - The original description to refine
   * @returns Observable with refined description
   */
  refineDescription(description: string): Observable<any> {
    const formData = new FormData();
    formData.append('description', description);

    return this.http.post(`${environment.url}/ai/time-entry/refine`, formData);
  }

  /**
   * Refine multiple time entry descriptions using AI in batch
   *
   * @param descriptions - Array of descriptions to refine
   * @returns Observable with batch refinement results
   */
  refineBatch(descriptions: string[]): Observable<any> {
    const formData = new FormData();

    // Send each description as a separate form field
    descriptions.forEach((description, index) => {
      formData.append(`descriptions[${index}]`, description);
    });

    return this.http.post(`${environment.url}/ai/time-entry/refine-batch`, formData);
  }

  /**
   * Refine multiple time entry descriptions using AI in batch with custom prompt
   *
   * @param descriptions - Array of descriptions to refine
   * @param promptTemplate - Custom prompt template to use for refinement
   * @returns Observable with batch refinement results
   */
  refineBatchWithPrompt(descriptions: string[], promptTemplate: string, temperature?: number): Observable<any> {
    const formData = new FormData();

    // Send each description as a separate form field
    descriptions.forEach((description, index) => {
      formData.append(`descriptions[${index}]`, description);
    });

    // Add custom prompt template
    formData.append('prompt_template', promptTemplate);

    // Add custom temperature if provided
    if (temperature !== undefined) {
      formData.append('temperature', temperature.toString());
    }

    return this.http.post(`${environment.url}/ai/time-entry/refine-batch`, formData);
  }
}
